import React from 'react'
import { Platform, Text, type Role, type TextProps, type View } from 'react-native'

import { cn } from '@/src/general/utils/cn'
import type { TTextContrast } from '@/src/styles/colors'

import { typographyVariants, type TTypographyVariantProps } from './typography-variants'

export type TTypographyProps = {
  className?: string
  bold?: boolean
  black?: boolean
  italic?: boolean
  center?: boolean
  contrast?: TTextContrast
  ariaRole?: Role | 'span' | 'div' | 'paragraph'
  tabIndex?: number
  heading?: keyof typeof HEADING_TYPES
  ref?: React.Ref<View>
} & TextProps &
  Omit<TTypographyVariantProps, 'variant'>

const HEADING_TYPES = {
  h1: { 'aria-level': 1 },
  h2: { 'aria-level': 2 },
  h3: { 'aria-level': 3 },
  h4: { 'aria-level': 4 },
  h5: { 'aria-level': 5 },
  h6: { 'aria-level': 6 },
}

export const Typography = ({
  ref,
  className,
  center,
  bold,
  black,
  italic,
  size,
  colors,
  contrast,
  children,
  ariaRole = Platform.select({ web: 'paragraph', native: 'none' }),
  heading,
  selectable = true,
  ...otherProps
}: TTypographyProps) => {
  const headingProps = heading ? HEADING_TYPES[heading] : {}
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- react-native-web role interop
  const role = Platform.select({
    web: heading ? 'heading' : ariaRole === 'div' ? null : ariaRole,
    native: 'none',
  }) as Role
  return (
    <Text
      ref={ref}
      className={cn(typographyVariants({ bold, black, colors, size, contrast, center, italic }), className)}
      style={{ userSelect: selectable ? 'text' : 'none' }}
      // Please see : https://necolas.github.io/react-native-web/docs/accessibility/#semantic-html
      role={role}
      {...headingProps}
      {...otherProps}
    >
      {children}
    </Text>
  )
}

Typography.displayName = 'Typography'
